/**
 * Browser-only image processing helpers.
 *
 * Resizes any image (data URL, blob URL, or remote URL) to fit within
 * MAX_DIM and re-encodes as JPEG at QUALITY. Returns a Blob ready to upload.
 */

const MAX_DIM = 1600;
const QUALITY = 0.82;

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    // crossOrigin is required so we can paint remote images to a canvas
    // without tainting it. Sources that don't permit CORS will throw — we
    // surface that to the caller so the UI can fall back gracefully.
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = src;
  });

const drawToBlob = (
  img: HTMLImageElement,
  maxDim = MAX_DIM,
  quality = QUALITY,
): Promise<Blob> => {
  const ratio = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * ratio));
  const h = Math.max(1, Math.round(img.naturalHeight * ratio));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D unavailable");
  ctx.drawImage(img, 0, 0, w, h);

  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Image encode failed"))),
      "image/jpeg",
      quality,
    ),
  );
};

/** Resize + JPEG-encode an image source. Source can be a File, data URL, blob URL, or http(s) URL. */
export const processImage = async (
  source: File | string,
): Promise<Blob> => {
  let src: string;
  if (typeof source === "string") {
    src = source;
  } else {
    src = URL.createObjectURL(source);
  }
  try {
    const img = await loadImage(src);
    return await drawToBlob(img);
  } finally {
    if (typeof source !== "string") URL.revokeObjectURL(src);
  }
};

/** Helper: read a Blob as a data URL (used for previews). */
export const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error("Could not read image"));
    r.readAsDataURL(blob);
  });
